import {
  retrieveWorkflowInvitations,
  type WorkflowInvitation,
} from "@/lib/data/requests/connections/workflow-invitations";
import { completeOwnedConnections } from "@/lib/data/requests/connections/owned-connections";
import type { Connection } from "@/lib/types";
import { authenticatedFastApiAxiosInstance } from "@/lib/data/http";

export type SignatureRequestListType = "firm" | "matter";
export type SignatureRequestListStatus = "pending" | "completed" | "expired";

export type RetrieveSignatureRequestsV2Input = {
  type?: SignatureRequestListType;
  status?: SignatureRequestListStatus;
  pageNumber?: number;
  pageSize?: number;
};

export type SignatureRequestField = {
  id: string;
  firmId: string;
  signatureRequestId: string;
  signerType: "contact" | "user";
  signerId: string;
  type: "signature" | "initials" | "date" | "text" | "checkbox" | "attachment";
  label: string;
  required: boolean;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  defaultValue: string | null;
  placeholder: string | null;
  validationMaxLength: number | null;
  validationPattern: string | null;
  dateCreated: string;
  dateModified: string;
};

export type MatterSignatureRequestField = SignatureRequestField & {
  matterId: string;
};

export type FirmSignatureRequestListItemV2 = {
  dateCreated: string;
  dateModified: string;
  id: string;
  firmId: string;
  fileId: string;
  fileIdDestination: string | null;
  expirationDate: string;
  name: string | null;
  presignedUrlKey: string | null;
  variables: unknown;
  invitations?: WorkflowInvitation[];
  contactConnections: Array<{
    id: string;
    firmId: string;
    contactId: string;
    label: string;
    signatureRequestPasscode?: string;
    signatureRequestComplete: boolean;
    signerOrder: number | null;
    pendingSigner: boolean;
    dateCreated: string;
    dateModified: string;
    contactName: string | null;
    contactEmails: Array<{ id: string; emailAddress: string | null }>;
    contactPhones: Array<{
      id: string;
      number: string | null;
      extension?: string | null;
    }>;
  }>;
  userConnections: Array<{
    id: string;
    userId: string;
    label: string;
    signatureRequestPasscode?: string;
    signatureRequestComplete: boolean;
    signerOrder: number | null;
    pendingSigner: boolean;
    dateCreated: string;
    dateModified: string;
    user: {
      id: string;
      email: string | null;
      firstName: string | null;
      middleName: string | null;
      lastName: string | null;
    } | null;
  }>;
  fields: SignatureRequestField[];
};

type MatterSignatureRequestUserConnection = {
  id: string;
  matterId: string;
  firmId: string;
  userId: string;
  label: string;
  signatureRequestPasscode?: string;
  signatureRequestComplete: boolean;
  signerOrder: number | null;
  pendingSigner: boolean;
  dateCreated: string;
  dateModified: string;
  signatureRequestId: string | null;
  email: string | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
  user: {
    id: string;
    email: string | null;
    firstName: string | null;
    middleName: string | null;
    lastName: string | null;
    phoneNumber: string | null;
  } | null;
};

export type MatterSignatureRequestListItemV2 = {
  dateCreated: string;
  dateModified: string;
  id: string;
  firmId: string;
  matterId: string;
  fileId: string | null;
  fileIdDestination: string | null;
  expirationDate: string | null;
  name: string | null;
  presignedUrlKey: string | null;
  invitations?: WorkflowInvitation[];
  connections: Connection[];
  connectionsNextCursor?: string | null;
  fields: MatterSignatureRequestField[];
  statutesOfLimitations: Array<{
    id: string;
    date: string;
    confirmed: boolean;
    description: string | null;
    isPrimary: boolean;
  }>;
  variables: unknown;
  signedFileId: string | null;
  userConnections: MatterSignatureRequestUserConnection[] | null;
  workingFileKey: string | null;
};

type SignatureRequestPage = {
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
};

export type SignatureRequestListV2Response = {
  data: {
    firmSignatureRequests: FirmSignatureRequestListItemV2[];
    matterSignatureRequests: MatterSignatureRequestListItemV2[];
  };
  meta: { userMsg: string };
  pagination: {
    firmSignatureRequests?: SignatureRequestPage;
    matterSignatureRequests?: SignatureRequestPage;
  };
};

export const retrieveSignatureRequestsV2 = (
  params: RetrieveSignatureRequestsV2Input = {},
) =>
  authenticatedFastApiAxiosInstance
    .get<SignatureRequestListV2Response>("signature-requests/v4", { params })
    .then(async (res) => {
      const hydrate = async <
        T extends {
          id: string;
          connections: Connection[];
          connectionsNextCursor?: string | null;
          matterId?: string;
        },
      >(
        request: T,
        family: "firms" | "matters",
      ) => {
        const path = `${family}/v4/signature-requests/${request.id}`;
        const complete = await completeOwnedConnections(
          request,
          path,
          request.matterId ?? "",
        );
        return {
          ...complete,
          invitations: await retrieveWorkflowInvitations(
            path,
            request.matterId,
          ),
        };
      };
      const firmSignatureRequests = [];
      for (const request of res.data.data.firmSignatureRequests)
        firmSignatureRequests.push({
          ...request,
          invitations: await retrieveWorkflowInvitations(
            `firms/v4/signature-requests/${request.id}`,
          ),
        });
      const matterSignatureRequests = [];
      for (const request of res.data.data.matterSignatureRequests)
        matterSignatureRequests.push(await hydrate(request, "matters"));
      return {
        ...res.data,
        data: { firmSignatureRequests, matterSignatureRequests },
      };
    });
